import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatch } from 'react-router-dom';
import { resources } from '../../core/services/resources';
import type { ReportTemplate } from '../../core/types/data-models';

/**
 * Resolves the "currently selected" report template for the sidebar.
 *
 * Selection cascades — but only across templates that belong to the
 * active LOB. Templates from other LOBs never win, even if the URL
 * still points at one — that's the case when the user switches LOB
 * mid-flow while sitting on a detail page from the prior LOB.
 *
 *   1. The URL's `:id` *if* the template belongs to the active LOB.
 *   2. Otherwise the first template tagged against the active LOB,
 *      so the per-template sub-rows (Settings / Prompts / Instances)
 *      have a target.
 *   3. `null` — no templates exist for this LOB. The sub-rows render
 *      disabled in this case.
 *
 * The fetch shares its query key with the sidebar dropdown so we don't
 * pay for two network round-trips on the same data.
 */
export function useSelectedReportTemplate(lineOfBizId: number | null): {
  effectiveId: number | null;
  templates: ReportTemplate[];
  isLoading: boolean;
} {
  const templatesQuery = useQuery({
    queryKey: ['report-templates', 'select'],
    queryFn: () => resources.reportTemplates.list({ pageSize: 200, sort: 'name' }),
  });
  const all = templatesQuery.data?.data ?? [];

  const templates = useMemo(
    () => (lineOfBizId == null ? [] : all.filter((t) => t.lineOfBizId === lineOfBizId)),
    [all, lineOfBizId],
  );

  const match = useMatch('/report-templates/:id/*');
  const urlId = match?.params.id ? Number(match.params.id) : null;

  const effectiveId = useMemo(() => {
    // The URL id only "wins" when it points at a template in the
    // currently-active LOB — otherwise the sidebar would advertise a
    // template that isn't reachable from the current LOB scope.
    if (urlId != null && templates.some((t) => t.id === urlId)) return urlId;
    return templates[0]?.id ?? null;
  }, [urlId, templates]);

  return { effectiveId, templates, isLoading: templatesQuery.isLoading };
}
